export function createFukushizaiGroupActionService(deps) {
    // detail 113 から対象 group の行を除去します。
    function removeFukushizaiGroupFromDetailRows(itemCd, groupNo) {
        const item = deps.normalizeItemCd(itemCd);
        const group = deps.normalizeGroupNo(groupNo);
        if (item === "" || group === "") return false;

        const hitKey = Object.keys(deps.entrydetailRef.current || {}).find((key) => deps.normalizeTypeKey(key) === deps.FUKUSHIZAI_BASE_PANEL_TYPE)
            || deps.FUKUSHIZAI_BASE_PANEL_TYPE;
        const rows = Array.isArray(deps.entrydetailRef.current?.[hitKey]) ? deps.entrydetailRef.current[hitKey] : [];
        const nextRows = rows.filter((row) => {
            if (deps.isEmpty(row)) return false;
            return !deps.sameItemGroup(row, item, group);
        });
        if (nextRows.length === rows.length) return false;

        deps.entrydetailRef.current[hitKey] = nextRows;
        const panelInfo = deps.resolveGridPanelByType(deps.FUKUSHIZAI_BASE_PANEL_TYPE);
        if (!deps.isEmpty(panelInfo)) {
            deps.setGridData(panelInfo, deps.filterRowsByFukushizaiContextIfKeyed(nextRows, deps.selectedFukushizaiContextRef.current));
        }
        return true;
    }

    // 削除後に移動候補となる次 group を決めます。
    function nextFukushizaiGroupAfterDelete(itemCd, deletedGroupNo) {
        const deleted = deps.normalizeGroupNo(deletedGroupNo);
        const groups = deps.getKnownFukushizaiGroupNos(itemCd)
            .filter((value) => deps.normalizeGroupNo(value) !== deleted)
            .map((value) => deps.normalizeGroupNo(value))
            .filter((value, index, list) => value !== "" && list.indexOf(value) === index)
            .sort((a, b) => Number(a) - Number(b) || a.localeCompare(b));

        if (groups.length === 0) return "";
        const larger = groups.find((value) => Number(value) > Number(deleted));
        return larger || groups[groups.length - 1] || "";
    }

    // dialog 入力から group 追加を実行します。
    function applyFukushizaiGroupAddFromDialog() {
        const dialog = deps.getGroupDialogState();
        const itemCd = deps.normalizeKakousijiUiString(dialog.itemCd || deps.getSelectedSetItem());
        const groupNo = deps.normalizeEditableGroupNo(dialog.groupNo);
        const colorCodes = deps.checkedGroupDialogColorCodes();

        if (itemCd === "" || groupNo === "") return false;
        if (deps.getKnownFukushizaiGroupNos(itemCd).indexOf(groupNo) !== -1) return false;

        deps.setGroupOptions((prev) => {
            const next = [...new Set([...(Array.isArray(prev) ? prev : []), groupNo]
                .map((value) => deps.normalizeGroupNo(value))
                .filter((value) => value !== ""))];
            return next.sort((a, b) => Number(a) - Number(b) || a.localeCompare(b));
        });

        // 色指定ありなら base 行と registered group をまとめて補完します。
        if (colorCodes.length !== 0) {
            deps.ensureFukushizaiBaseKeyRowsForColors(itemCd, groupNo, colorCodes);
            deps.syncRegisteredGroupRowsForContext(itemCd, groupNo, colorCodes, "edit");
        } else {
            deps.ensureRegisteredGroupRowForContext(itemCd, groupNo);
        }

        deps.fukushizaiGroupClearedByUserRef.current = false;
        deps.refreshAfterFukushizaiGroupMutation(itemCd, groupNo, true);
        return true;
    }

    // 副資材(113)側のグループ削除。品質表示(118-124)側には触れない(品質側は独立して削除する)。
    function applyFukushizaiGroupDelete(itemCd, groupNo) {
        const normalizedItem = deps.normalizeItemCd(itemCd || deps.getSelectedSetItem());
        const normalizedGroup = deps.normalizeGroupNo(groupNo || deps.getSelectedGroup());
        if (normalizedItem === "" || normalizedGroup === "") return false;

        let changed = removeFukushizaiGroupFromDetailRows(normalizedItem, normalizedGroup);
        if (deps.syncRegisteredGroupRowsForContext(normalizedItem, normalizedGroup, [], "delete")) changed = true;
        deps.setGroupOptions((prev) => (Array.isArray(prev) ? prev : [])
            .filter((value) => deps.normalizeGroupNo(value) !== normalizedGroup));
        const nextGroup = nextFukushizaiGroupAfterDelete(normalizedItem, normalizedGroup);
        deps.refreshAfterFukushizaiGroupMutation(normalizedItem, nextGroup || normalizedGroup, true);
        return changed;
    }

    // dialog 入力から group 編集を実行します。
    function applyFukushizaiGroupEditFromDialog() {
        const dialog = deps.getGroupDialogState();
        const itemCd = deps.normalizeItemCd(dialog.itemCd || deps.getSelectedSetItem());
        const groupNo = deps.normalizeGroupNo(dialog.groupNo || deps.getSelectedGroup());
        const checkedColorsList = deps.checkedGroupDialogColorCodes();
        if (itemCd === "" || groupNo === "") return false;

        const baseSnapshotRows = deps.DeepCopy(deps.getFukushizaiRowsByContext(itemCd, groupNo));

        const beforeColors = deps.fukushizaiColorCodesForContext(itemCd, groupNo);
        const checkedColors = new Set(checkedColorsList.map((value) => deps.normalizeColorCd(value)).filter((value) => value !== ""));
        const removedColors = beforeColors.filter((colorCd) => !checkedColors.has(colorCd));

        // 副資材(113)側のみ編集する。品質表示(118-124)側は品質側のグループ編集で独立して操作する。
        let changed = deps.rebuildFukushizaiBaseRowsForEditedGroup(itemCd, groupNo, baseSnapshotRows, checkedColorsList);

        let registeredChanged = deps.syncRegisteredGroupRowsForGroupEdit(itemCd, groupNo, [...checkedColors]);
        if (checkedColorsList.length === 0 && deps.ensureRegisteredGroupRowForContext(itemCd, groupNo)) {
            registeredChanged = true;
        }

        const flagChanged = deps.updateSagefudaItemFlags([itemCd]);
        if (!changed && !registeredChanged && !flagChanged) return false;

        deps.debugFukushizaiSyncLog("[Kakousiji][fukushizai group edit applied]", {
            targetItem: itemCd,
            targetGroup: groupNo,
            beforeColors,
            afterColors: checkedColorsList,
            removedColors,
            addedColors: checkedColorsList.filter((colorCd) => beforeColors.indexOf(colorCd) === -1),
            representativeBaseRowFound: baseSnapshotRows.length !== 0
        });

        deps.refreshAfterFukushizaiGroupMutation(itemCd, groupNo, changed || registeredChanged || flagChanged);
        return changed || registeredChanged || flagChanged;
    }

    return {
        removeFukushizaiGroupFromDetailRows,
        nextFukushizaiGroupAfterDelete,
        applyFukushizaiGroupAddFromDialog,
        applyFukushizaiGroupDelete,
        applyFukushizaiGroupEditFromDialog
    };
}
